import { useState } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, Eye, EyeOff, Loader2, Shield, Mail, CheckCircle, AlertTriangle } from 'lucide-react';
import { storage } from '@/lib/storage';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  
  // Get the 'next' parameter from URL (where to redirect after login)
  const nextUrl = searchParams.get('next');
  // Check if session actually expired (401 from server) vs just not logged in
  const sessionExpired = searchParams.get('expired') === 'true';
  
  // Set page title
  usePageTitle('login');
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: login form, 2: 2FA verification
  const [twoFACode, setTwoFACode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const texts = {
    pt: {
      welcomeBack: 'Bem-vindo de volta',
      enterCredentials: 'Entre com suas credenciais',
      email: 'Email',
      emailPlaceholder: 'seu@email.com',
      password: 'Senha',
      forgotPassword: 'Esqueceu a senha?',
      loginButton: 'Entrar',
      loading: 'Entrando...',
      noAccount: 'Não tem conta?',
      signUp: 'Cadastre-se',
      loginSuccess: 'Login realizado com sucesso!',
      invalidCredentials: 'Credenciais inválidas',
      sessionExpired: 'Sua sessão expirou. Por favor, faça login novamente.',
      // 2FA texts
      twoFATitle: 'Verificação em 2 Fatores',
      twoFADesc: 'Enviamos um código de 6 dígitos para',
      enterCode: 'Digite o código',
      codePlaceholder: '000000',
      verify: 'Verificar',
      verifying: 'Verificando...',
      invalidCode: 'Código inválido',
      codeExpired: 'Código expirado',
      backToLogin: 'Voltar ao login',
      adminAccess: 'Acesso Administrativo',
    },
    en: {
      welcomeBack: 'Welcome back',
      enterCredentials: 'Enter your credentials',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      password: 'Password',
      forgotPassword: 'Forgot password?',
      loginButton: 'Sign In',
      loading: 'Signing in...',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      loginSuccess: 'Login successful!',
      invalidCredentials: 'Invalid credentials',
      sessionExpired: 'Your session has expired. Please log in again.',
      // 2FA texts
      twoFATitle: 'Two-Factor Verification',
      twoFADesc: 'We sent a 6-digit code to',
      enterCode: 'Enter the code',
      codePlaceholder: '000000',
      verify: 'Verify',
      verifying: 'Verifying...',
      invalidCode: 'Invalid code',
      codeExpired: 'Code expired',
      backToLogin: 'Back to login',
      adminAccess: 'Admin Access',
    }
  };

  const txt = texts[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const emailLower = formData.email.trim().toLowerCase();

    try {
      // First, try admin login (silently - don't show error if user is not admin)
      let isAdmin = false;
      let adminData = null;
      
      try {
        const adminResponse = await fetch(`${API_URL}/api/auth/admin-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: emailLower,
            password: formData.password,
          }),
        });

        if (adminResponse.ok) {
          adminData = await adminResponse.json();
          isAdmin = true;
        }
        // If not admin (403/401), silently continue to regular login
      } catch (adminErr) {
        // Network error on admin login - silently continue to regular login
        console.log('Admin login not available, trying regular login...');
      }

      if (isAdmin && adminData) {
        if (adminData.requires_2fa) {
          // Admin user - needs 2FA verification
          setPendingEmail(emailLower);
          setStep(2);
          toast.success(language === 'pt' ? 'Código 2FA enviado para seu email!' : '2FA code sent to your email!');
          setLoading(false);
          return;
        }

        // Admin login successful WITHOUT 2FA (2FA disabled)
        if (adminData.token) {
          // Clear any previous user data using storage utility
          storage.clearAuth();

          // Save new auth data using storage utility
          storage.setToken(adminData.token);
          storage.setUser(adminData.user);
          
          toast.success(language === 'pt' ? 'Login realizado com sucesso!' : 'Login successful!');
          
          // Redirect admin to admin dashboard (or to nextUrl if provided)
          const redirectTo = nextUrl && nextUrl.startsWith('/admin') ? nextUrl : '/admin';
          navigate(redirectTo);
          setLoading(false);
          return;
        }
      }

      // Not admin or admin login failed - do regular login
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailLower,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || txt.invalidCredentials);
      }

      // Clear any previous user data to prevent cache issues
      const keysToPreserve = ['app_version', 'language'];
      const preservedData = {};
      keysToPreserve.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) preservedData[key] = value;
      });
      
      // Clear localStorage but preserve non-user settings
      localStorage.clear();
      Object.entries(preservedData).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      // Save token and user using storage utility
      storage.setToken(data.token);
      storage.setUser(data.user);

      toast.success(txt.loginSuccess);
      
      // Redirect to nextUrl if provided, otherwise to home
      const redirectTo = nextUrl || '/';
      navigate(redirectTo);
      
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.message || txt.invalidCredentials;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (twoFACode.length !== 6) {
      toast.error(txt.invalidCode);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/admin-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingEmail,
          code: twoFACode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || txt.invalidCode);
      }

      // Clear any previous user data to prevent cache issues
      const keysToPreserve = ['app_version', 'language'];
      const preservedData = {};
      keysToPreserve.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) preservedData[key] = value;
      });
      
      // Clear localStorage but preserve non-user settings
      localStorage.clear();
      Object.entries(preservedData).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      // Save token and user
      // Save auth data using storage utility
      storage.setToken(data.token);
      storage.setUser(data.user);

      toast.success(txt.loginSuccess);
      
      // Redirect to nextUrl if provided, otherwise to admin
      const redirectTo = nextUrl && nextUrl.startsWith('/admin') ? nextUrl : '/admin';
      navigate(redirectTo);
      
    } catch (err) {
      console.error('2FA verification error:', err);
      const errorMessage = err.message || txt.invalidCode;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep(1);
    setTwoFACode('');
    setPendingEmail('');
    setError('');
  };

  // Step 2: 2FA Verification
  if (step === 2) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-[#F5F7FA] via-[#EAF8FD] to-[#F1FBF7] py-12 px-4 sm:px-6">
          <Card className="w-full max-w-md bg-white rounded-xl shadow-[0_8px_24px_rgba(8,12,20,0.08)] border-0" data-testid="2fa-form">
            <CardHeader className="space-y-3 text-center pt-8">
              <div className="flex justify-center mb-2">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500/10 to-orange-600/10 rounded-2xl flex items-center justify-center">
                  <Shield className="w-8 h-8 text-orange-500" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-[#08172F]">{txt.twoFATitle}</CardTitle>
              <CardDescription className="text-[#6B7280]">
                <span className="flex items-center justify-center gap-1 mb-1">
                  <Shield className="w-4 h-4 text-orange-500" />
                  {txt.adminAccess}
                </span>
                {txt.twoFADesc}<br />
                <strong className="text-[#08172F]">{pendingEmail}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="2fa-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="code">{txt.enterCode}</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder={txt.codePlaceholder}
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  autoFocus
                  data-testid="2fa-code-input"
                />
              </div>

              <Button
                onClick={handleVerify2FA}
                className="w-full bg-orange-500 text-white hover:bg-orange-600"
                disabled={loading || twoFACode.length !== 6}
                data-testid="2fa-verify-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {txt.verifying}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {txt.verify}
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-[#00C2D1] hover:underline"
                >
                  ← {txt.backToLogin}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 1: Login Form
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-[#F5F7FA] via-[#EAF8FD] to-[#F1FBF7] py-12 px-4 sm:px-6">
        <Card className="w-full max-w-md bg-white rounded-xl shadow-[0_8px_24px_rgba(8,12,20,0.08)] border-0" data-testid="login-form">
          <CardHeader className="space-y-3 text-center pt-8">
            <div className="flex justify-center mb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00C2D1]/10 to-[#00A8B5]/10 rounded-2xl flex items-center justify-center">
                <Home className="w-8 h-8 text-[#00C2D1]" />
              </div>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold font-['Montserrat'] text-[#08172F]">
              {txt.welcomeBack}
            </CardTitle>
            <CardDescription className="text-sm md:text-base text-[#6B7280] font-['Poppins']">
              {txt.enterCredentials}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Session expired alert - only show when session actually expired (401) */}
              {sessionExpired && (
                <Alert variant="warning" className="bg-amber-50 border-amber-200 text-amber-800" data-testid="session-expired-alert">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    {txt.sessionExpired}
                  </AlertDescription>
                </Alert>
              )}
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">{txt.email}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={txt.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">{txt.password}</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-[#00C2D1] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    {txt.forgotPassword}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-[#08172F] text-white hover:bg-[#0B1E3C] rounded-lg shadow-[0_4px_12px_rgba(8,12,20,0.06)] transition-all duration-200 h-11 font-medium"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {txt.loading}
                  </>
                ) : (
                  txt.loginButton
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">{txt.noAccount} </span>
              <Link to="/register" className="text-[#00C2D1] hover:underline font-medium">
                {txt.signUp}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
